import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {AxiosError} from 'axios'
import {useAuth} from '../core/Auth'
import * as authHelper from '../core/AuthHelpers'
import {getUserByToken, autologin} from '../core/_requests'
import {AuthModel, SmsChallengeResponse} from '../core/_models'
import {ensureDeviceId} from '../core/device'

const AutoLogin = () => {
  const params = useParams()
  const navigate = useNavigate()
  const {saveAuth, setCurrentUser} = useAuth()
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<string | null>(null)
  const [requiresSms, setRequiresSms] = useState(false)
  const [smsCode, setSmsCode] = useState('')
  const [deviceId] = useState(() => ensureDeviceId())
  const token = params?.token

  const executeAutoLogin = async (code?: string) => {
    if (!token) {
      setStatus('Geçersiz bağlantı.')
      setLoading(false)
      return
    }

    setLoading(true)
    setStatus(null)

    try {
      saveAuth(undefined)
      setCurrentUser(undefined)
      authHelper.removeAuthRoles()

      const response = await autologin(token, {deviceId, smsCode: code})
      const responseData = response.data as AuthModel[] | SmsChallengeResponse

      if ((responseData as SmsChallengeResponse).requiresSms) {
        const challenge = responseData as SmsChallengeResponse
        setRequiresSms(true)
        const messageText = challenge.message ?? 'SMS doğrulaması gerekli.'
        const phoneInfo = challenge.phone ? ` (${challenge.phone})` : ''
        setStatus(`${messageText}${phoneInfo}`)
        setLoading(false)
        return
      }

      const authResponse = responseData as AuthModel[]
      const firstRoleAuth = authResponse[0]
      if (!firstRoleAuth) {
        throw new Error('Token alınamadı.')
      }

      saveAuth(firstRoleAuth)
      authHelper.setAuthRoles({
        roles: authResponse,
        activeRoleIndex: 0,
      })

      const {data: user} = await getUserByToken(firstRoleAuth.token)
      setCurrentUser(user)
      navigate('/')
    } catch (err) {
      const error = err as AxiosError<SmsChallengeResponse & {message?: string}>
      console.error(error)

      if (error.response) {
        const data = error.response.data
        if (data?.requiresSms) {
          setRequiresSms(true)
          const messageText = data.message ?? 'SMS doğrulaması gerekli.'
          const phoneInfo = data.phone ? ` (${data.phone})` : ''
          setStatus(`${messageText}${phoneInfo}`)
          setLoading(false)
          return
        }

        setStatus(data?.message ?? 'Giriş işlemi tamamlanamadı.')
      } else {
        setStatus('Giriş işlemi tamamlanamadı.')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (token) {
      executeAutoLogin()
    } else {
      setLoading(false)
      setStatus('Geçersiz bağlantı.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedCode = smsCode.trim()
    await executeAutoLogin(trimmedCode.length > 0 ? trimmedCode : undefined)
    setSmsCode('')
  }

  if (!requiresSms) {
    return <>{loading ? 'Yükleniyor...' : status}</>
  }

  return (
    <form className='form w-100' onSubmit={handleSubmit}>
      <div className='text-dark fw-bold mb-5'>{status}</div>
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>SMS Doğrulama Kodu</label>
        <input
          type='text'
          autoComplete='one-time-code'
          className='form-control form-control-lg form-control-solid'
          placeholder='SMS kodu'
          value={smsCode}
          onChange={(event) => setSmsCode(event.target.value)}
          disabled={loading}
        />
      </div>
      <div className='d-flex gap-3'>
        <button
          type='submit'
          className='btn btn-dark flex-grow-1'
          disabled={loading || smsCode.trim().length === 0}
        >
          Doğrula
        </button>
        <button
          type='button'
          className='btn btn-light flex-grow-1'
          onClick={() => executeAutoLogin()}
          disabled={loading}
        >
          Kodu Yeniden Gönder
        </button>
      </div>
    </form>
  )
}

export {AutoLogin}
